import React, { useEffect, useState } from 'react';
import { Copy, X, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Modal that renders a scannable QR code for the user's Grow Along invite
 * URL. Great for in-person sharing at a garden center, plant swap, or with
 * a friend across the room — they just point their phone at your screen.
 *
 * Fully client-side: uses the `qrcode` npm package to generate a data-URL
 * PNG and renders it in an <img>. No backend, no image upload.
 */
export const InviteQrModal = ({ url, onClose }) => {
  // Accept either a plain string (legacy) or an object shape with truncation
  // metadata from `buildInviteUrl({ maxUrlLength })`.
  const inviteUrl = typeof url === 'string' ? url : url?.url;
  const includedCount = typeof url === 'object' && url ? url.includedCount : null;
  const totalCount = typeof url === 'object' && url ? url.totalCount : null;
  const truncated = typeof url === 'object' && url ? url.truncated : false;
  const title = (typeof url === 'object' && url && url.title) || 'Scan to open my library';

  const [dataUrl, setDataUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!inviteUrl) return;
    let cancelled = false;
    (async () => {
      try {
        // Browser-safe entry (main entry pulls in Node `fs` which CRA can't polyfill).
        const qrcodeMod = await import('qrcode/lib/browser');
        const toDataURL = qrcodeMod.toDataURL || qrcodeMod.default?.toDataURL;
        if (typeof toDataURL !== 'function') {
          throw new Error('QR renderer unavailable in this browser');
        }
        const png = await new Promise((resolve, reject) => {
          toDataURL(inviteUrl, {
            width: 288,
            margin: 2,
            // EC-L gives us the largest data capacity (~2953 bytes at V40) —
            // fine for on-screen display where the code won't get damaged.
            errorCorrectionLevel: 'L',
            color: { dark: '#2d523c', light: '#ffffff' },
          }, (err, u) => (err ? reject(err) : resolve(u)));
        });
        if (!cancelled) setDataUrl(png);
      } catch (err) {
        console.error('QR render failed:', err);
        if (!cancelled) setError(err?.message || 'Could not render QR code');
      }
    })();
    return () => { cancelled = true; };
  }, [inviteUrl]);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteUrl);
      } else {
        window.prompt('Copy the invite link:', inviteUrl);
      }
      setCopied(true);
      toast.success('Invite link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy — long-press the URL below to copy manually');
    }
  };

  return (
    <Dialog open={!!inviteUrl} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-sm"
        style={{ backgroundColor: 'white' }}
        data-testid="invite-qr-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-display" style={{ color: 'hsl(143, 28%, 25%)' }}>
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Scannable QR code for the Grow Along invite link. Includes a copy button and the raw URL.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-2">
          <div
            className="rounded-xl p-3 border flex items-center justify-center"
            style={{ borderColor: 'hsl(143, 28%, 85%)', backgroundColor: 'white', width: 296, height: 296 }}
          >
            {error ? (
              <div className="text-sm text-red-600 text-center" data-testid="qr-error">
                {error}
              </div>
            ) : dataUrl ? (
              <img
                src={dataUrl}
                width={288}
                height={288}
                alt="Invite QR code"
                data-testid="qr-image"
              />
            ) : (
              <div className="text-sm" style={{ color: 'hsl(125, 12%, 32%)' }} data-testid="qr-loading">
                Generating…
              </div>
            )}
          </div>

          {truncated && includedCount != null && totalCount != null ? (
            <p
              className="text-xs text-center px-3 py-2 rounded-lg w-full"
              style={{ backgroundColor: 'hsl(45, 90%, 94%)', color: 'hsl(30, 40%, 25%)' }}
              data-testid="qr-truncation-notice"
            >
              This invite shows <strong>{includedCount} of {totalCount}</strong> plants — a partial preview of your collection.
            </p>
          ) : (
            <p className="text-xs text-center px-2" style={{ color: 'hsl(125, 12%, 32%)' }}>
              Point a phone camera at the code, or copy the link below.
            </p>
          )}

          <div
            className="w-full rounded-lg px-3 py-2 font-mono text-xs break-all"
            style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}
            data-testid="qr-url-text"
          >
            {inviteUrl}
          </div>

          <div className="flex gap-2 w-full">
            <Button
              onClick={handleCopy}
              variant="outline"
              className="flex-1"
              style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
              data-testid="qr-copy-button"
            >
              {copied ? (
                <><Check className="w-4 h-4 mr-2" />Copied</>
              ) : (
                <><Copy className="w-4 h-4 mr-2" />Copy link</>
              )}
            </Button>
            <Button
              onClick={onClose}
              className="flex-1"
              style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
              data-testid="qr-close-button"
            >
              <X className="w-4 h-4 mr-2" />Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteQrModal;
